import React, { useState, useRef } from 'react';
import Cropper from 'react-easy-crop';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import { useSelector } from 'react-redux';

import SimpleModal from 'modules/shared/components/simple-modal';
import { ImageTypes } from 'constants/image-types';
import {
    submitProfilePicture,
    deleteProfilePicture,
    selectProfilePicture,
} from 'modules/settings/stores/user-settings-slice-handler';
import getCroppedImg from 'utils/crop-image';
import t from 'utils/translate';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import LabelWithSiblings from 'modules/shared/components/label-with-siblings';
import { selectCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import classNames from 'classnames';
import { PROFILE_IMAGE_MAX_SIZE } from 'constants/user-settings';
import { addSnackbar } from 'utils/dialogs-manager';
import SnackbarTypes from 'constants/snackbar-types';
import IconButton from '@mui/material/IconButton';
import ContextMenu from '../../shared/components/context-menu';

function SideBarProfilePicture() {
    const { AllowUserModificationsFromClient } = useSelector(selectCompanySettings);
    const { url: profilePictureUrl, dataIsLoading } = useSelector(selectProfilePicture);

    const [tempProfilePicture, setTempProfilePicture] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const inputFile = useRef(null);
    const [showModal, toggleModalVisibility] = useState(false);

    const increaseZoom = () => setZoom((z) => (z >= 3 ? z : z + 0.1));
    const decreaseZoom = () => setZoom((z) => (z <= 1 ? z : z - 0.1));

    const handlePhotoUpload = () => {
        if (AllowUserModificationsFromClient) {
            inputFile.current.click();
        }
    };

    const handlePhotoRemove = async () => {
        await deleteProfilePicture();
    };

    const handlePhotoSelection = async (event) => {
        const selectedImage = event.target.files[0];
        if (selectedImage.size / (1024 * 1024) > PROFILE_IMAGE_MAX_SIZE) {
            addSnackbar({
                type: SnackbarTypes.ERROR,
                title: t('settings_profile_image'),
                message: t('settings_profile_image_update_size_error'),
            });
            return;
        }

        const validImageTypes = Object.values(ImageTypes);

        if (!validImageTypes.some((imageType) => imageType === selectedImage.type)) {
            addSnackbar({
                type: SnackbarTypes.ERROR,
                title: t('settings_profile_image'),
                message: t('settings_profile_image_update_format_error'),
            });
            return;
        }

        setTempProfilePicture(URL.createObjectURL(selectedImage));
        toggleModalVisibility(!showModal);
    };

    const handlePhotoSubmissionAbort = () => {
        URL.revokeObjectURL(tempProfilePicture);
        toggleModalVisibility(!showModal);
    };

    const handlePhotoSubmission = async () => {
        try {
            const { croppedImage, encodedImage } = await getCroppedImg(tempProfilePicture, croppedAreaPixels);
            await submitProfilePicture(encodedImage, croppedImage);
        } catch (e) {
            // This will catch possible errors from getCroppedImg
            console.error(e);
            addSnackbar({
                type: SnackbarTypes.ERROR,
                title: t('settings_profile_image'),
                message: t('settings_profile_image_update_server_error'),
            });
        }

        toggleModalVisibility(!showModal);
    };

    const onCropComplete = (croppedArea, newCroppedAreaPixels) => setCroppedAreaPixels(newCroppedAreaPixels);

    return (
        <>
            <section className="profile-picture-container">
                <div className="profile-picture-title">
                    {AllowUserModificationsFromClient ? (
                        <LabelWithInfo
                            text={t('settings_profile_image')}
                            info={
                                <>
                                    <span>{t('settings_profile_image_update_format_error')}</span>
                                    <br />
                                    <span>{t('settings_profile_image_update_size_error')}</span>
                                </>
                            }
                        />
                    ) : (
                        <LabelWithSiblings labelContent={t('settings_profile_image')} siblingsMask="L" />
                    )}
                </div>
                <input
                    hidden
                    type="file"
                    ref={inputFile}
                    accept={Object.values(ImageTypes).toString()}
                    onChange={handlePhotoSelection}
                    className="hidden"
                />
                <div className="profile-picture-wrapper">
                    <Badge
                        overlap="circular"
                        className="avatar-badge"
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        badgeContent={
                            AllowUserModificationsFromClient &&
                            profilePictureUrl && (
                                <ContextMenu
                                    trigger={
                                        <IconButton
                                            className="profile-picture-menu-trigger"
                                            variant="outlined"
                                            size="medium"
                                        >
                                            <span className="stw-edit-pen"></span>
                                        </IconButton>
                                    }
                                    position={'bottom'}
                                    items={[
                                        {
                                            callback: handlePhotoUpload,
                                            text: t('js_all_upload_photo'),
                                            glyphFontIcon: 'stw-upload-picture',
                                        },
                                        {
                                            callback: handlePhotoRemove,
                                            text: t('js_all_remove_photo'),
                                            glyphFontIcon: 'stw-remove-picture',
                                        },
                                    ]}
                                />
                            )
                        }
                    >
                        <Avatar src={profilePictureUrl} className="profile-picture-avatar">
                            <div
                                className={classNames({
                                    'picture-container': true,
                                    pointer: AllowUserModificationsFromClient,
                                    'default-picture': profilePictureUrl.length === 0 && !dataIsLoading,
                                    'with-preloader': dataIsLoading,
                                })}
                                onClick={handlePhotoUpload}
                                data-testid="picture-container"
                            ></div>
                        </Avatar>
                    </Badge>
                </div>
                {showModal && (
                    <SimpleModal
                        dialogTitle={t('settings_profile_image')}
                        buttons={
                            <div className="profile-picture-modal-actions">
                                <Button
                                    variant="contained"
                                    role="cancel"
                                    onClick={handlePhotoSubmissionAbort}
                                    className="cancel-button"
                                >
                                    {t('js_all_cancel')}
                                </Button>

                                <Button variant="contained" role="primary" onClick={handlePhotoSubmission}>
                                    {t('js_all_message_ack')}
                                </Button>
                            </div>
                        }
                    >
                        <Box className="cropper-container">
                            <Cropper
                                image={tempProfilePicture}
                                crop={crop}
                                zoom={zoom}
                                aspect={1 / 1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                cropShape="round"
                                zoomWithScroll={false}
                                showGrid={false}
                            />

                            <Box className="zoom-buttons-container">
                                <Button variant="contained" onClick={increaseZoom} className="zoom-button">
                                    +
                                </Button>

                                <Button variant="contained" onClick={decreaseZoom} className="zoom-button">
                                    -
                                </Button>
                            </Box>
                        </Box>
                    </SimpleModal>
                )}
            </section>
        </>
    );
}

export default SideBarProfilePicture;
